/**
 * Estate Verification Hooks
 *
 * React Query hooks for death verification request and status operations.
 *
 * @module hooks/useEstateVerification
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { useAuth } from '@xanectech/auth';
import {
  EstateVerificationService,
  type CreateVerificationRequestPayload,
  type ReviewerQueueOptions,
  type ApprovePayload,
  type RejectPayload,
  type EscalatePayload,
} from '../services/EstateVerificationService';
import { createLogger } from '@xanectech/lib';

const logger = createLogger('useEstateVerification');

// ============================================
// Query keys
// ============================================

export const verificationKeys = {
  list: (requesterId: string) =>
    ['estate', 'verification', 'list', requesterId] as const,
  ownerList: (userId: string) =>
    ['estate', 'verification', 'owner', userId] as const,
  detail: (requestId: string) =>
    ['estate', 'verification', requestId] as const,
  reviewerQueue: (statuses: string[]) =>
    ['estate', 'verification', 'reviewer', 'queue', ...statuses] as const,
  auditLog: (requestId: string) =>
    ['estate', 'verification', 'audit', requestId] as const,
};

// ============================================
// Owner / requester hooks
// ============================================

/**
 * List verification requests submitted by the authenticated user (as requester).
 */
export function useVerificationRequests() {
  const { user } = useAuth();
  const userId = user?.id ?? '';

  return useQuery({
    queryKey: verificationKeys.list(userId),
    queryFn: () => EstateVerificationService.listVerificationRequests(userId),
    enabled: !!userId,
  });
}

/**
 * List verification requests targeting the authenticated user's estate (as owner).
 */
export function useOwnerVerificationRequests() {
  const { user } = useAuth();
  const userId = user?.id ?? '';

  return useQuery({
    queryKey: verificationKeys.ownerList(userId),
    queryFn: () => EstateVerificationService.listRequestsForOwner(userId),
    enabled: !!userId,
  });
}

/**
 * Fetch a single verification request by ID.
 */
export function useVerificationRequest(requestId: string) {
  return useQuery({
    queryKey: verificationKeys.detail(requestId),
    queryFn: () => EstateVerificationService.getVerificationRequest(requestId),
    enabled: !!requestId,
  });
}

/**
 * Create a new verification request (submitted by a legacy contact/requester).
 */
export function useCreateVerificationRequest() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const userId = user?.id ?? '';

  return useMutation({
    mutationFn: (payload: CreateVerificationRequestPayload) =>
      EstateVerificationService.createVerificationRequest(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: verificationKeys.list(userId),
      });
    },
    onError: (error) => {
      logger.error('Failed to create verification request', {
        context: 'useCreateVerificationRequest',
        error: String(error),
      });
    },
  });
}

// ============================================
// Reviewer hooks (role-gated at component level)
// ============================================

/**
 * List verification requests queued for manual reviewer action.
 * Components using this hook MUST enforce reviewer role access.
 */
export function useReviewerVerificationQueue(
  options: ReviewerQueueOptions = {},
) {
  const statuses = options.statuses ?? ['submitted', 'under_review'];

  return useQuery({
    queryKey: verificationKeys.reviewerQueue(statuses),
    queryFn: () => EstateVerificationService.listReviewerQueue(options),
  });
}

/**
 * Fetch the audit log for a specific verification request.
 * Components using this hook MUST enforce reviewer role access.
 */
export function useVerificationAuditLog(requestId: string) {
  return useQuery({
    queryKey: verificationKeys.auditLog(requestId),
    queryFn: () =>
      EstateVerificationService.getVerificationAuditLog(requestId),
    enabled: !!requestId,
  });
}

/**
 * Approve a verification request.
 * Components using this hook MUST enforce reviewer role access.
 */
export function useApproveVerification() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Omit<ApprovePayload, 'actorId'>) =>
      EstateVerificationService.approveVerificationRequest({
        ...payload,
        actorId: user?.id ?? '',
      }),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({
        queryKey: verificationKeys.detail(data.id),
      });
      void queryClient.invalidateQueries({
        queryKey: ['estate', 'verification', 'reviewer'],
      });
      void queryClient.invalidateQueries({
        queryKey: verificationKeys.auditLog(data.id),
      });
    },
    onError: (error) => {
      logger.error('Failed to approve verification request', {
        context: 'useApproveVerification',
        error: String(error),
      });
    },
  });
}

/**
 * Reject a verification request.
 * Components using this hook MUST enforce reviewer role access.
 */
export function useRejectVerification() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Omit<RejectPayload, 'actorId'>) =>
      EstateVerificationService.rejectVerificationRequest({
        ...payload,
        actorId: user?.id ?? '',
      }),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({
        queryKey: verificationKeys.detail(data.id),
      });
      void queryClient.invalidateQueries({
        queryKey: ['estate', 'verification', 'reviewer'],
      });
      void queryClient.invalidateQueries({
        queryKey: verificationKeys.auditLog(data.id),
      });
    },
    onError: (error) => {
      logger.error('Failed to reject verification request', {
        context: 'useRejectVerification',
        error: String(error),
      });
    },
  });
}

/**
 * Escalate a verification request.
 * Components using this hook MUST enforce reviewer role access.
 */
export function useEscalateVerification() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Omit<EscalatePayload, 'actorId'>) =>
      EstateVerificationService.escalateVerificationRequest({
        ...payload,
        actorId: user?.id ?? '',
      }),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({
        queryKey: verificationKeys.detail(data.id),
      });
      void queryClient.invalidateQueries({
        queryKey: ['estate', 'verification', 'reviewer'],
      });
      void queryClient.invalidateQueries({
        queryKey: verificationKeys.auditLog(data.id),
      });
    },
    onError: (error) => {
      logger.error('Failed to escalate verification request', {
        context: 'useEscalateVerification',
        error: String(error),
      });
    },
  });
}
